import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DialogComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { DataFieldGroup } from '@app/models/settings/data-field-group.model';
import { FieldGroupForm } from '@app/modules/forms/settings/components';
import { SettingsMenuService } from '@app/modules/settings/services/settings-menu.service';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { IndexView } from '@common/views';
import { CustomFieldGroupsIndexTable, DefaultFieldGroupsIndexTable } from '@settings/services';

@Component({
    templateUrl: './field-groups-index.template.html',
    styleUrls: ['./field-groups-index.style.scss'],
})
export class FieldGroupsIndexView extends IndexView implements OnInit {
    @ViewChild('fieldGroupDeleteModal', { static: true }) fieldGroupDeleteModal: DialogComponent;
    @ViewChild('fieldGroupModal', { static: true }) fieldGroupModal: DialogComponent;
    @ViewChild('fieldGroupForm', { static: true }) fieldGroupForm: FieldGroupForm;

    customFieldGroupsLoading = true;
    defaultFieldGroupsLoading = true;
    deleteConfirmationBody = '';
    fieldGroupToDelete: DataFieldGroup;

    constructor(
        public customFieldGroupsIndexTable: CustomFieldGroupsIndexTable,
        public defaultFieldGroupsIndexTable: DefaultFieldGroupsIndexTable,
        private auth: AuthService,
        private router: Router,
        private notify: NotifyService,
        private analyticsService: AnalyticService,
        public settingsMenuService: SettingsMenuService
    ) {
        super();
    }

    get isViewLoading(): boolean {
        if (this.customFieldGroupsLoading) {
            return true;
        }

        return this.defaultFieldGroupsLoading;
    }

    async ngOnInit(): Promise<void> {
        this.loadTables();
    }

    loadTables(): void {
        this.loadCustomFieldGroupsTable();
        this.loadDefaultFieldGroupsTable();
    }

    loadCustomFieldGroupsTable(): void {
        this.customFieldGroupsIndexTable.onLoading.subscribe(
            (isLoading) => (this.customFieldGroupsLoading = isLoading)
        );
        this.customFieldGroupsIndexTable.reload();
    }

    loadDefaultFieldGroupsTable(): void {
        this.defaultFieldGroupsIndexTable.onLoading.subscribe(
            (isLoading) => (this.defaultFieldGroupsLoading = isLoading)
        );
        this.defaultFieldGroupsIndexTable.reload();
    }

    createCustomFieldGroup(fieldGroup: DataFieldGroup, fieldGroupForm: FieldGroupForm): void {
        if (fieldGroupForm.form.invalid) {
            return;
        }

        fieldGroup
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.analyticsService.trackEvent(AnalyticEvents.SettingsModuleCreateCustomFieldGroup);

                this.cancel();
                this.customFieldGroupsIndexTable.reload();
            })
            .catch(() => {
                this.notify.error('This field group name is already in use');
            });
    }

    cancel(): void {
        this.fieldGroupForm.reset();
        this.fieldGroupModal.open = false;
    }

    menuAction(event: MenuColumnEvent): void {
        const { menuItem, data } = event;
        const fieldGroup = data as DataFieldGroup;
        switch (menuItem.event) {
            case 'delete':
                this.openFieldGroupDeletionModal(fieldGroup);
                break;
            case 'edit':
                this.router.navigate(['/settings', 'custom-fields', 'field-groups', fieldGroup.id, 'edit']);
                break;
        }
    }

    openFieldGroupDeletionModal(fieldGroup: DataFieldGroup): void {
        this.fieldGroupToDelete = fieldGroup;
        const dataFieldsCount = this.fieldGroupToDelete.dataFieldCount;

        if (dataFieldsCount === 0) {
            this.deleteConfirmationBody =
                'There are no fields in this group. This action cannot be reversed.' +
                '<br><br>' +
                `Are you sure you want to delete the field group "${fieldGroup.name}"?`;

            this.fieldGroupDeleteModal.open = true;
            return;
        }

        this.deleteConfirmationBody = `This field group contains <strong>${dataFieldsCount}</strong> custom field${
            dataFieldsCount === 1 ? '' : 's'
        }. Please remove all fields from this group before deleting.`;

        this.fieldGroupDeleteModal.open = true;
    }

    onGoToFields(): void {
        this.router.navigate(['/settings/custom-fields/fields']);
    }

    async onDeleteFieldGroup(): Promise<void> {
        this.customFieldGroupsLoading = true;
        await this.fieldGroupToDelete
            .param('company', this.auth.company.id)
            .delete()
            .then(() => {
                this.analyticsService.trackEvent(AnalyticEvents.SettingsModuleDeleteCustomFieldGroup);
                this.customFieldGroupsIndexTable.reload();
            })
            .catch((error) => {
                this.notify.error(error);
            });
        this.fieldGroupDeleteModal.open = false;
        this.customFieldGroupsLoading = false;
    }
}
